import { Finder } from 'Custom/Finder';
import Image from 'Nori/Image';
import Layout from 'Nori/Layout';
import Button from 'Nori/Button';
import SplitContent from 'Custom/Split/SplitContent';
import HeroFull from 'Nori/Hero/HeroFull';
import RichText from 'Custom/RichText';
import AjustableImage from 'Custom/AjustableImage';
import ItemBreakpoint from 'Custom/ItemBreakpoint';
import EffectiveSplit from 'Custom/EffectiveSplit';
import HERO_BG_IMAGE from 'Images/background/pink-marble.png';
import HERO_BG_IMAGE_MOBILE from 'Images/background/pink-marble-mobile.png';
import UTERUS_IMAGE from 'Images/misc/uterus-image.png';
import HAND_IMAGE_DESKTOP from 'Images/misc/hand-desktop.webp';
import HAND_IMAGE_MOBILE from 'Images/misc/hand-mobile.webp';
import PLUS_IMAGE_DESKTOP from 'Images/misc/plus-sign-desktop.webp';
import styles from 'styles/pages/howDoesNexplanonWorks.module.scss';

const HERO_UTERUS_IMAGE = {
  src: UTERUS_IMAGE,
  alt: "Diagram Showing How NEXPLANON® (etonogestrel implant) 68 mg Radiopaque Works in a Woman's Reproductive System",
};

const HERO_UTERUS_IMAGE_MOBILE = {
  src: UTERUS_IMAGE,
  alt: "Diagram Showing How NEXPLANON® (etonogestrel implant) 68 mg Radiopaque Works in a Woman's Reproductive System",
};

const HEAD = {
  title:
    'How Does NEXPLANON® (etonogestrel implant) 68 mg Radiopaque Work? | Birth Control Implant Explained',
  description:
    'Discover how NEXPLANON® (etonogestrel implant) 68 mg Radiopaque works, how long it lasts, and where to find a trained healthcare professional for insertion.',
  keywords:
    'How does Nexplanon work, Nexplanon Hormones, How Nexplanon prevents Pregnancy',
};

export default function HowDoesNexplanonWorks() {
  const HeroBannerContent = () => (
    <>
      <RichText
        className={styles.heroCopy}
        data-qa-tag="richtext-how-nexplanon-works"
      >
        <h4 data-qa-tag="heading-how-nexplanon-works">HOW NEXPLANON WORKS</h4>
        <h2 data-qa-tag="subheading-how-nexplanon-works">
          <span
            className={styles.topColoredText}
            data-qa-tag="highlight-nexplanon"
          >
            NEXPLANON
          </span>{' '}
          works to prevent pregnancy in several ways.
        </h2>
        <p data-qa-tag="paragraph-intro">
          Once it gets inserted, it releases a steady, low dose of progestin, a
          hormone that works to prevent pregnancy in 3 ways:
        </p>
        <ul className="primary-list" data-qa-tag="list-prevention-methods">
          <li className="graphic-red" data-qa-tag="list-item-stop-ovulation">
            <strong data-qa-tag="strong-stop-ovulation">Stops ovulation</strong>{' '}
            by preventing the release of an egg from the ovary.
          </li>
          <li className="graphic-blue" data-qa-tag="list-item-thin-lining">
            <strong data-qa-tag="strong-thin-lining">
              Thins the lining of the uterus
            </strong>
            , which makes implantation of the egg more difficult.
          </li>
          <li className="graphic-yellow" data-qa-tag="list-item-thicken-mucus">
            <strong data-qa-tag="strong-thicken-mucus">
              Thickens mucus in the cervix
            </strong>
            , which makes it harder for the sperm to reach the egg.
          </li>
        </ul>
      </RichText>
    </>
  );

  return (
    <Layout head={HEAD}>
      <div className={styles.heroSplit} data-qa-tag="hero-split-container">
        <ItemBreakpoint breakpoint="lg" data-qa-tag="item-breakpoint-lg">
          <HeroFull image={HERO_BG_IMAGE} data-qa-tag="hero-full-desktop">
            <SplitContent
              reverse
              data-design-visible="visible_vd"
              data-design-type="module"
              data-design-nori_id="Inline Content"
              data-design-module_name="NEXPLANON is just as effective as the pill - without the daily hassle"
              data-design-module_no="01"
              data-qa-tag="split-content-desktop"
            >
              <div
                className={styles.uterusImage}
                data-qa-tag="uterus-image-wrapper"
              >
                <Image
                  width={557}
                  height={397}
                  src={HERO_UTERUS_IMAGE.src}
                  alt={HERO_UTERUS_IMAGE.alt}
                  data-qa-tag="image-uterus-desktop"
                />
              </div>
              <HeroBannerContent />
            </SplitContent>
          </HeroFull>

          <HeroFull image={HERO_BG_IMAGE_MOBILE} data-qa-tag="hero-full-mobile">
            <SplitContent
              reverse
              data-design-visible="visible_vd"
              data-design-type="module"
              data-design-nori_id="Inline Content"
              data-design-module_name="NEXPLANON is just as effective as the pill - without the daily hassle"
              data-design-module_no="01"
              data-qa-tag="split-content-mobile"
            >
              <div
                className={styles.uterusImage}
                data-qa-tag="uterus-image-wrapper"
              >
                <Image
                  width={557}
                  height={397}
                  src={HERO_UTERUS_IMAGE_MOBILE.src}
                  alt={HERO_UTERUS_IMAGE_MOBILE.alt}
                  data-qa-tag="image-uterus-mobile"
                />
              </div>
              <HeroBannerContent />
            </SplitContent>
          </HeroFull>
        </ItemBreakpoint>
      </div>

      <EffectiveSplit moduleNumber="02" />

      <SplitContent
        backgroundColor={'colored'}
        id="nexplanon-works-preventing-ovulation"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="NEXPLANON works by preventing ovulation"
        data-design-module_no="03"
        padding="no_padding"
        data-qa-tag="split-content-insertion"
      >
        <ItemBreakpoint breakpoint="lg" data-qa-tag="item-breakpoint-insertion">
          <AjustableImage
            size="full"
            image={{ src: HAND_IMAGE_DESKTOP, style: { width: 376 }, alt: '' }}
            data-qa-tag="image-hand-desktop"
          />
          <AjustableImage
            size="full"
            image={{ src: HAND_IMAGE_MOBILE, style: { width: 187 }, alt: '' }}
            data-qa-tag="image-hand-mobile"
          />
        </ItemBreakpoint>
        <>
          <RichText data-qa-tag="richtext-insertion">
            <h2 className={styles.adjustMargin} data-qa-tag="heading-insertion">
              NEXPLANON is inserted during an outpatient office visit
            </h2>
            <p className="intro-sm" data-qa-tag="paragraph-insertion">
              Typical insertion takes a few minutes<sup>#</sup> and is performed
              under a local anesthetic. In clinical studies, 5.2% of patients
              felt insertion-site pain. NEXPLANON can be removed at any time
              during the 5-year period.
            </p>
            <p className="caption" data-qa-tag="caption-insertion">
              <sup>#</sup>In a clinical trial, the mean insertion time (from
              the removal of the protection cap of the applicator until
              retraction of the needle from the arm) was 27.9 ± 29.3 seconds.
            </p>
          </RichText>
          <Button
            href="/insertion/"
            data-design-type="clickAction"
            data-design-category="button_vd"
            data-design-action="Button"
            data-design-label="Show Me How It Works"
            data-design-nori_id="Inline Content"
            data-design-module_name="NEXPLANON works by preventing ovulation"
            data-qa-tag="button-learn-insertion"
            data-design-module_no="03"
          >
            Learn More About Insertion
          </Button>
        </>
      </SplitContent>

      <SplitContent
        reverse
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="side effects of NEXPLANON"
        data-design-module_no="04"
        data-qa-tag="split-content-side-effects"
      >
        <ItemBreakpoint
          breakpoint="lg"
          className={styles.plusImage}
          data-qa-tag="item-breakpoint-side-effects"
        >
          <AjustableImage
            size="full"
            image={{ src: PLUS_IMAGE_DESKTOP, style: { width: 292 }, alt: '' }}
            data-qa-tag="image-plus-desktop"
          />
          <AjustableImage
            size="full"
            image={{ src: PLUS_IMAGE_DESKTOP, style: { width: 146 }, alt: '' }}
            data-qa-tag="image-plus-mobile"
          />
        </ItemBreakpoint>
        <>
          <RichText data-qa-tag="richtext-side-effects">
            <h2 data-qa-tag="heading-side-effects">
              Side effects of NEXPLANON
            </h2>
            <p className="intro-sm" data-qa-tag="paragraph-side-effects">
              The most common side effect of NEXPLANON is a change in your
              normal menstrual bleeding pattern. In studies, 1 in 10 women
              stopped using NEXPLANON because of an unfavorable change in their
              bleeding pattern. Besides changes in menstrual bleeding patterns,
              other frequent side effects that caused women to stop using the
              implant include mood swings, weight gain, headache, acne, and
              depressed mood.
            </p>
          </RichText>
          <Button
            href="/side-effects/"
            data-design-type="clickAction"
            data-design-category="button_vd"
            data-design-action="Button"
            data-design-label=" Side Effects of NEXPLANON"
            data-design-nori_id="Inline Content"
            data-design-module_name="side effects of NEXPLANON"
            data-qa-tag="button-side-effects"
            data-design-module_no="04"
          >
            Side Effects of NEXPLANON
          </Button>
        </>
      </SplitContent>

      <Finder data-design-module_no="05" data-qa-tag="finder-component" />
    </Layout>
  );
}
